import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { enqueueSnackbar } from 'actions/notifications.actions';
import {
  clearPendingSsoError,
  pendingSsoErrorCode,
  shouldDeferSsoErrorFlash,
  ssoErrorMessageKey,
} from 'core/utils/sso-error';
import {
  appConfigSelector,
  configErrorSelector,
  isAuthenticatedSelector,
} from 'reducers/app.reducer';

/** Affiche la cause d'un échec de connexion à la boutique. */
export const SsoErrorFlash = (): null => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { appLocale } = useSelector(appConfigSelector);
  const configError = useSelector(configErrorSelector);

  // `appLoading` ne garantit pas que changeLanguage soit passé : on attend la
  // langue elle-même, sinon le message part figé en français dans le store.
  const localeReady = appLocale
    ? i18n.language === appLocale
    : Boolean(configError);

  useEffect(() => {
    if (!localeReady) {
      return;
    }

    const code = pendingSsoErrorCode();

    if (code === null) {
      return;
    }

    if (shouldDeferSsoErrorFlash(isAuthenticated, window.location.pathname)) {
      return;
    }

    clearPendingSsoError();

    dispatch(
      enqueueSnackbar({
        message: t(ssoErrorMessageKey(code)),
        options: {
          key: `sso-error-${code}`,
          variant: 'error',
          // La page d'arrivée peut rester derrière l'overlay du header partagé
          // aussi longtemps que la durée de vie d'un snackbar.
          persist: true,
        },
      }),
    );
  }, [dispatch, isAuthenticated, localeReady, t]);

  return null;
};

export default SsoErrorFlash;
